/**
 * The caller's invite link, built from their metasite so the signup it produces is ATTRIBUTED to
 * them. Shared 1:1 with the native apps — iOS `SymphonyData.referralShareLink`, Android
 * `SymphonyApi.referralShareLink`, and the server's `referrals` native-data action all build the
 * same string, so an invite earns the same credit whoever sent it.
 *
 * Web was the lone surface sharing an UNATTRIBUTED link (`APP_SMART_LINK`, a bare install
 * smart-link with no referrer in it), which is why a web share earned the sharer nothing.
 *
 * ⚠️ The metasite must stay a PATH segment, never a query param. The links service generates the
 * install payload from `pathParams` only, so `?deeplink=…` is dropped and the referrer is lost
 * silently — see the long note on the server's `referrals` action for the measurements.
 *
 * Returns '' for a caller with no metasite. That empty string is meaningful: callers must treat it
 * as "we do not know this person's link yet" and refuse to offer it, exactly as iOS disables its
 * share CTA on `shareLink.isEmpty`. Handing out a half-known link is worse than offering none —
 * the recipient signs up and the referral attributes to nobody.
 */
import { isReferralShortCode } from './referral-capture';

const REFERRAL_LINK_HOST = 'https://symphony.wix.app';

/** Where a visitor who cannot open the app is sent. Hard-coded on purpose: this URL is pasted into
 *  someone else's chat, so it can never be derived from the request host (a dev origin would ship). */
export const SYMPHONY_WEB_ORIGIN = 'https://symphony.wix.com';

/**
 * Two links-service parameters, and where a link carries the tail at all, BOTH are required — `d` alone
 * silently breaks phones. A bare CODE link carries neither, because the service defaults both for that
 * one shape (see `linkPath`); every other form still needs them explicitly.
 *
 * `d` names where a visitor who cannot open the app is sent, and without it that visitor is passed to the
 * vendor's install page and on to a generic marketing page which discards the referrer.
 *
 * `s` (`suggestMobile`) is what keeps `d` off phones. The service resolves a redirect in a fixed order,
 * and `d` is consulted for EVERY platform: `mobileUrl.filter(isMobileOS).orElse(desktopUrl)`. The
 * store-link resolver ahead of it is gated on `suggestMobile && isMobileOS`, so with `s` unset a phone
 * without the app falls through to `d` and lands on the web instead of the App Store — losing the one
 * attribution path that works today. With `s` set, a phone takes the store link (referrer preserved in
 * its `?deeplink=`) and only a desktop reaches `d`.
 *
 * Verified against production per platform: iPhone and Android reach their stores carrying the referrer,
 * a desktop reaches our signup carrying the id.
 *
 * Neither disturbs a phone that HAS the app: it intercepts the universal link by host + path before any
 * request is made, and both apps read the referrer from the PATH, passing unknown query keys through
 * (`jotActionTarget` / `jotActionForSegments`).
 */
const SUGGEST_MOBILE = 's=true';

function desktopFallbackUrl(publicRef: string): string {
  return `${SYMPHONY_WEB_ORIGIN}/welcome?ref=${encodeURIComponent(publicRef)}`;
}

/**
 * The identifier that goes in the link: the server-delivered short CODE when we have one, the metaSiteId
 * when we do not. A non-empty code is TRUSTED, not shape-checked — it arrives from `/api/referrals`
 * (minted `[a-z2-9]{8}` today, and the deployed links-service route also admits dotted vanity codes), and
 * the server and both natives already emit `r/<code>` for any non-empty code, so a web-only shape gate
 * here would hand the same account a different link per surface. `isReferralShortCode` stays the test for
 * an identifier of UNKNOWN provenance (a captured `?ref=` — see `installLink`), never for this field.
 *
 * Falling back rather than refusing is deliberate: an unavailable code service should cost a link its
 * BREVITY, never its existence. The empty-metasite contract above is unchanged — no metasite still
 * means no link, because that is genuinely unattributable, whereas a missing code is not.
 */
function deliveredCode(referralCode?: string | null): string {
  return referralCode?.trim() ?? '';
}

/**
 * The PREFIX follows the identifier: a code rides the short `r/` route, a metaSiteId keeps the legacy
 * `referral/` one. Both native route parsers have admitted `r/` since #8195, and the flip to emitting it
 * waited on those binaries being what the field actually runs (~91% iOS / ~93% Android of weekly-active
 * accounts, measured 2026-09-11 — see docs/referral-short-links-plan.md).
 *
 * `r/` is deliberately reserved for CODES, and the reason is live behaviour, not tidiness: the links
 * service defaults `suggestMobile` and `d=welcome?ref=<code>` for a PARAM-LESS `r/<code>` path on the
 * Symphony hosts (catalyst-server #23802, links-server 1.7909.0, GA 2026-08-16). That default fires only
 * under the `r/` prefix — the PREFIX is the whole protection — so a metasite link must stay on
 * `referral/` with its own explicit tail, or its desktop visitors land on the mobile invite page and the
 * referrer is lost. (The deployed gate is case-insensitive and admits dots/dashes/underscores inside the
 * code, so do not reason from the identifier's characters; only the prefix separates the two worlds.)
 */
function linkPath(ref: string): string {
  return isReferralShortCode(ref) ? `r/${ref}` : `referral/${ref}`;
}

/**
 * The invite link bare — for a target that can only ever be reached from a phone, such as a QR code.
 * Naming a desktop destination there would encode a longer string into a denser code for a case that
 * cannot arise, and a code that is harder to scan is a worse invite. It needs no `s` either: with neither
 * parameter set, the service's own fallback resolver already hands a phone the store link.
 */
export function referralInstallLink(metaSiteId: string | undefined | null, referralCode?: string | null): string {
  if (!metaSiteId) return '';
  const code = deliveredCode(referralCode);
  return `${REFERRAL_LINK_HOST}/${code ? `r/${code}` : `referral/${metaSiteId}`}`;
}

/**
 * A get-the-app link that carries an invite when there is one, and is otherwise unchanged.
 *
 * `fallback` is where this control points for someone who arrived on their own — a store page, the plain
 * install smart-link — and it stays the destination off a phone, because that is what `?d=` names.
 *
 * It routes through `symphony.wix.app` rather than pointing at a store directly, and that is the whole
 * mechanism: the referrer is recorded when the click passes through the links service, which then hands
 * the phone a store URL carrying it for the first-launch lookup. A store URL with the referrer appended
 * by us would skip the service, so nothing would be recorded.
 *
 * This one keeps the explicit tail EVEN for a code, unlike `referralShareLink`: the service's bare-link
 * default points desktop at the welcome capture page, but this control's desktop destination is the
 * caller's own `fallback` (a store page, the install smart-link) — explicit params win over the default,
 * which is exactly what preserves it.
 *
 * `ref` here is a CAPTURED identifier of unknown shape (a stored `?ref=` — GUID from a legacy link, code
 * from a short one), which is why this is the one caller that shape-detects via `linkPath` instead of
 * being told which it holds.
 */
export function installLink(ref: string | null | undefined, fallback: string): string {
  if (!ref) return fallback;
  return `${REFERRAL_LINK_HOST}/${linkPath(ref)}?${SUGGEST_MOBILE}&d=${encodeURIComponent(fallback)}`;
}

export function referralShareLink(metaSiteId: string | undefined | null, referralCode?: string | null): string {
  if (!metaSiteId) return '';
  // A code link is BARE — the links service supplies `s` and `d=welcome?ref=<code>` itself for a
  // param-less `r/<code>` (see `linkPath`). Restating them would only spend back the length the code
  // bought. The code-less fallback builds BOTH its legs from the metaSiteId — the one identifier this
  // branch has — so the path and the desktop `?d=` can never name different referrers.
  if (deliveredCode(referralCode)) return referralInstallLink(metaSiteId, referralCode);
  const d = encodeURIComponent(desktopFallbackUrl(metaSiteId));
  return `${referralInstallLink(metaSiteId)}?${SUGGEST_MOBILE}&d=${d}`;
}
